import {
  Activite,
  ActiviteRecensementState,
  ElementKey,
  ProchaineActivite,
  JopContext,
} from "@/models";
import { toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";
import { Http } from "@/utils/http";

/** Backend calls for the main activity timeline (list, filters, sign-up/"recensement"). */
export default class TimelineService {
  /** Fetches the current user's next upcoming activities. */
  public static async prochaineActivite(): Promise<ProchaineActivite[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=prochainesActivites`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }
  /** Lists activities, optionally filtered by activity type. */
  public static async activites(types: ElementKey[]): Promise<Activite[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=activites`;
    await Http.addPostSettings();
    const { data: response } = await Http.instance.post(
      targetUrl,
      Http.qsStringify({
        types,
      })
    );
    return response.data;
  }

  /** Lists all activities, unfiltered (used for admin/management views). */
  public static async activitesList(): Promise<Activite[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=activitesList`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /**
   * Records a volunteer's sign-up state ("recensement") for an activity — engaged or not available.
   * @param user_id Defaults to the current user server-side when omitted.
   * @param toastResult When true, shows a confirmation toast on success.
   */
  public static async recensement(
    activite: string,
    etat: ActiviteRecensementState,
    user_id?: string,
    toastResult = false
  ): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=recensement`;
    await Http.addPostSettings();
    const { data: response } = await Http.instance.post(
      targetUrl,
      Http.qsStringify({
        activite,
        etat,
        user_id,
      })
    );
    if (response.error) {
      toast.pushError(response.message ?? "Une erreur s'est produite.");
    }
    if (toastResult && !response.error) {
      toast.createAndPushToast(
        "Recensement",
        response.message,
        true,
        TYPE.DEFAULT
      );
      return true;
    }
    return response.data;
  }

  /**
   * Fetches JOP2024-related context.
   * @deprecated Leftover from the removed JOP2024/"V3" feature — not called by any view. See docs/FRONTEND.md.
   */
  public static async jop(): Promise<JopContext> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=jopContext`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /** Triggers a bulk re-import of activities from the source system (long-running, fires a toast when done). */
  public static async importActivites(): Promise<boolean> {
    toast.createAndPushToast(
      "Importation",
      "Importation des activités en cours...",
      true,
      TYPE.DEFAULT
    );
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=importActivites`;
    await Http.instance.get(targetUrl).then(({ data: response }) => {
      if (response.error) {
        toast.pushError(response.message ?? "Une erreur s'est produite.");
        return false;
      } else {
        toast.createAndPushToast(
          "Importation",
          response.message,
          true,
          TYPE.SUCCESS
        );
        return;
      }
      return false;
    });
    return false;
  }
}
